import { CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, HomeOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Empty, Modal, Select, Skeleton, Table, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  createGraphicPage,
  deleteGraphicPage,
  duplicateGraphicPage,
  listGraphicPages,
  siteAssetVariantUrl,
} from '../../../api'
import type { SaasApiError } from '../../../api'
import { VistoeraActionConfirm } from '../../../ui/action'
import {
  GRAPHIC_PAGE_STATUS_COLORS,
  GRAPHIC_PAGE_STATUS_LABELS,
  GRAPHIC_PAGE_TYPE_LABELS,
  type GraphicPage,
  type GraphicPageType,
} from '../../../graphicPageTypes'
import './graphic-editor.css'
import '../../../ui/tenant-items.css'

const CREATABLE_TYPES: { value: GraphicPageType; label: string }[] = [
  { value: 'exhibition_landing', label: '展览入口' },
  { value: 'research_landing', label: '研究入口' },
  { value: 'collection_landing', label: '主题收藏入口' },
  { value: 'custom', label: '自定义图文页面' },
]

/**
 * 图文编辑 — 图文页面列表 (spec §4). LiangCollection's own "展览入口/研究入口/
 * 主题收藏入口" are singleton page types (one row max per Site, see
 * `graphic_page_service.create_page`'s `SINGLETON_TYPES` re-use logic);
 * `custom` is the new general-purpose "图文故事页" type this migration
 * round adds on top (audit doc's "新的定位").
 */
export default function GraphicPagesTab({ workspaceId, siteId, siteBase }: { workspaceId: string; siteId: string; siteBase: string }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<GraphicPage[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newType, setNewType] = useState<GraphicPageType>('custom')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [homeBusy, setHomeBusy] = useState(false)

  const load = useCallback(() => {
    setError(null)
    listGraphicPages(workspaceId, siteId)
      .then((r) => setRows(r.data))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId])

  useEffect(load, [load])

  // EXISTING_PAGE_EDITOR_INTEGRATION round: "首页" is a `home`-type
  // singleton GraphicPage, pinned above the general list below (never
  // deletable/duplicable like a `custom` page — there is exactly one per
  // Site, same as the other landing types). If it doesn't exist yet, the
  // backend auto-creates it on first open and seeds it from whatever the
  // old `PagesTab` form had saved — see
  // `graphic_page_service._seed_home_page_from_legacy`. Nothing here ever
  // requires the user to "create a new page" to reach their real Home.
  const homeRow = rows?.find((p) => p.page_type === 'home') ?? null
  const otherRows = (rows ?? []).filter((p) => p.page_type !== 'home')

  const onOpenHome = async () => {
    if (homeRow) {
      navigate(`${siteBase}/graphic-pages/${homeRow.id}/edit`)
      return
    }
    setHomeBusy(true)
    try {
      const { data } = await createGraphicPage(workspaceId, siteId, { page_type: 'home' })
      navigate(`${siteBase}/graphic-pages/${data.id}/edit`)
    } catch (err) {
      message.error((err as SaasApiError).message || '打开首页失败')
    } finally {
      setHomeBusy(false)
    }
  }

  const onCreate = async () => {
    setCreating(true)
    try {
      const { data } = await createGraphicPage(workspaceId, siteId, { page_type: newType })
      setCreateOpen(false)
      navigate(`${siteBase}/graphic-pages/${data.id}/edit`)
    } catch (err) {
      message.error((err as SaasApiError).message || '创建失败')
    } finally {
      setCreating(false)
    }
  }

  const onDuplicate = async (page: GraphicPage) => {
    setBusyId(page.id)
    try {
      await duplicateGraphicPage(workspaceId, siteId, page.id)
      message.success('已复制')
      load()
    } catch (err) {
      message.error((err as SaasApiError).message || '复制失败')
    } finally {
      setBusyId(null)
    }
  }

  const deletePage = async (page: GraphicPage) => {
    setBusyId(page.id)
    try {
      await deleteGraphicPage(workspaceId, siteId, page.id)
      message.success('已删除')
      load()
    } catch (err) {
      message.error((err as SaasApiError).message || '删除失败')
      throw err
    } finally {
      setBusyId(null)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (rows === null) return <Skeleton active paragraph={{ rows: 6 }} style={{ marginTop: 16 }} />

  return (
    <div style={{ marginTop: 16 }}>
      <Card size="small" style={{ marginBottom: 16 }} className="ge-home-pin-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HomeOutlined style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>首页</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {homeRow
                ? `网站首页内容，通过统一编辑器维护 · ${GRAPHIC_PAGE_STATUS_LABELS[homeRow.status]}`
                : '网站首页内容 · 尚未在新编辑器中打开过（原有欢迎语/主图/精选内容不会丢失）'}
            </div>
          </div>
          <Button type="primary" icon={<EditOutlined />} loading={homeBusy} onClick={onOpenHome}>
            编辑首页
          </Button>
          {homeRow?.status === 'published' && (
            <Button icon={<EyeOutlined />} onClick={() => navigate(`${siteBase}/graphic-pages/${homeRow.id}/edit?preview=1`)}>
              预览
            </Button>
          )}
        </div>
      </Card>

      <div className="ge-list-toolbar">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          新建图文页面
        </Button>
      </div>
      {otherRows.length === 0 ? (
        <Empty description="暂无其他图文页面，点击“新建图文页面”开始创作" style={{ margin: '48px 0' }} />
      ) : (
        <Table<GraphicPage>
          className="tc-data-table"
          rowKey="id"
          dataSource={otherRows}
          pagination={false}
          sticky={{ offsetHeader: 64 }}
          columns={[
            {
              title: '封面',
              width: 84,
              render: (_, p) => (
                <div className="ge-list-cover">
                  {p.cover_asset_id ? (
                    <img
                      src={siteAssetVariantUrl(workspaceId, p.cover_asset_id, 'thumbnail')}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : (
                    '—'
                  )}
                </div>
              ),
            },
            {
              title: '标题',
              dataIndex: 'title',
              // P0-B spec §6: text-heavy column opts back to left.
              align: 'left' as const,
              render: (title, p) => (
                <div>
                  <a onClick={() => navigate(`${siteBase}/graphic-pages/${p.id}/edit`)}>{title || '（未命名）'}</a>
                  {p.subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.subtitle}</div>}
                </div>
              ),
            },
            {
              title: '页面类型',
              dataIndex: 'page_type',
              width: 140,
              render: (t: GraphicPageType) => GRAPHIC_PAGE_TYPE_LABELS[t] ?? t,
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 100,
              render: (s: GraphicPage['status']) => <Tag color={GRAPHIC_PAGE_STATUS_COLORS[s]}>{GRAPHIC_PAGE_STATUS_LABELS[s]}</Tag>,
            },
            {
              title: '前台展示',
              dataIndex: 'frontend_visible',
              width: 90,
              render: (v: boolean) => (v ? <Tag color="green">展示中</Tag> : <Tag>已隐藏</Tag>),
            },
            {
              title: '最后修改',
              dataIndex: 'updated_at',
              width: 160,
              render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
            },
            {
              title: '操作',
              width: 200,
              render: (_, p) => (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`${siteBase}/graphic-pages/${p.id}/edit`)}>
                    编辑
                  </Button>
                  <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`${siteBase}/graphic-pages/${p.id}/edit?preview=1`)}>
                    预览
                  </Button>
                  <Button size="small" icon={<CopyOutlined />} loading={busyId === p.id} onClick={() => onDuplicate(p)} />
                  <VistoeraActionConfirm
                    title="删除图文页面"
                    description={
                      p.status === 'draft'
                        ? `确认删除「${p.title}」？删除后不可恢复。`
                        : '已发布内容需先下架/归档，才能删除。'
                    }
                    confirmLabel="删除"
                    confirmDisabled={p.status !== 'draft'}
                    loading={busyId === p.id}
                    onConfirm={() => deletePage(p)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} aria-label="删除" />
                  </VistoeraActionConfirm>
                </div>
              ),
            },
          ]}
        />
      )}

      <Modal
        title="新建图文页面"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="创建"
        confirmLoading={creating}
      >
        <p style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>页面类型</p>
        <Select
          value={newType}
          onChange={setNewType}
          style={{ width: '100%' }}
          options={CREATABLE_TYPES.map((t) => ({ label: t.label, value: t.value }))}
        />
        <p style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)' }}>
          「展览入口」「研究入口」「主题收藏入口」每个网站仅可存在一个；若已存在，将直接打开现有页面进行编辑。
        </p>
      </Modal>
    </div>
  )
}
